import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getGenderPicklists } from 'services';
import handleError from 'utils/handleError';

export const GENDER_PICKLISTS_QUERY_KEY = 'gender-picklists';

interface Option {
  label: string;
  value: string;
}

const toAliasOptions = (items: Array<{ alias: string }> = []): Option[] =>
  items.map(({ alias }) => ({ label: alias, value: alias }));

const toNameOptions = (items: Array<{ name: string }> = []): Option[] =>
  items.map(({ name }) => ({ label: name, value: name }));

export function useGenderPicklists(propertyId?: number | string) {
  // Always source options from the API (Builder Preview included). When there is
  // no propertyId to query with, or the API returns nothing, the dropdown stays
  // blank instead of falling back to mock data.
  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: [GENDER_PICKLISTS_QUERY_KEY, propertyId],
    queryFn: () => getGenderPicklists(Number(propertyId)),
    enabled: Boolean(propertyId),
  });

  useEffect(() => {
    if (error) {
      handleError(
        `Form Viewer - Error loading gender picklists: ${error}`,
        'Error loading gender picklists.',
      );
    }
  }, [error]);

  const picklists = data?.data;

  const legalGenderOptions = useMemo(
    () => toAliasOptions(picklists?.legalGender),
    [picklists?.legalGender],
  );

  const genderIdentityOptions = useMemo(
    () => toNameOptions(picklists?.genderIdentity),
    [picklists?.genderIdentity],
  );

  const pronounsOptions = useMemo(() => toNameOptions(picklists?.pronouns), [picklists?.pronouns]);

  return {
    legalGenderOptions,
    genderIdentityOptions,
    pronounsOptions,
    isLoading: isLoading || isFetching,
    hasError: Boolean(error),
  };
}

export default useGenderPicklists;
